import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { usePlan } from "@/context/PlanContext";
import api, { formatApiError } from "@/lib/api";
import { loadConfig, calculateReport, CATEGORIES } from "@/lib/calcEngine";
import { downloadReport } from "@/lib/download";
import { formatCurrency, isoDate, MONTH_NAMES } from "@/lib/format";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, ChevronDown, CopyPlus, Wand2, Users2, CalendarOff,
  Save, Calculator, ArrowLeft, FileDown, Sheet, FileText, Upload, Lock, CheckCircle2,
} from "lucide-react";

const CAT_COLOR = { balvatika: "#DB2777", primary: "#2563EB", upper_primary: "#059669", high_school: "#EA580C" };
const CAT_SHORT = { balvatika: "Bal", primary: "Pri", upper_primary: "UP", high_school: "HS" };
const DAY_EMOJI = ["☀️", "🌙", "🔥", "🌿", "⭐", "🌸", "🪔"];
const HOLIDAY_REASONS = ["Government Holiday", "Sunday", "Local Holiday", "School Closed", "Other"];
const EMPTY = { balvatika: 0, primary: 0, upper_primary: 0, high_school: 0 };

const sumStudents = (s) => CATEGORIES.reduce((a, c) => a + (parseInt(s?.[c] || 0, 10) || 0), 0);

function loadYM() {
  try { const s = JSON.parse(localStorage.getItem("pm_bulk_ym")); if (s?.year && s?.month) return s; } catch { /* noop */ }
  const n = new Date();
  return { year: n.getFullYear(), month: n.getMonth() + 1 };
}

export default function BulkEntry() {
  const { t, lang } = useLang();
  const { hasAccess, openUpgrade, handle402 } = usePlan();
  const nav = useNavigate();
  const init = loadYM();

  const [ready, setReady] = useState(false);
  const [periodMode, setPeriodMode] = useState("month");   // month | range
  const [year, setYear] = useState(init.year);
  const [month, setMonth] = useState(init.month);
  const todayIso = isoDate(new Date());
  const [rangeStart, setRangeStart] = useState(todayIso);
  const [rangeEnd, setRangeEnd] = useState(todayIso);

  const [rows, setRows] = useState({});               // iso -> { day_type, students, payment_mode, holiday_reason, saved, savedSnap, error }
  const [costMap, setCostMap] = useState({});         // iso|sig -> { cost, rice } from the server (rates never in the browser)
  const [adminHolidays, setAdminHolidays] = useState(new Set());
  const [enrolled, setEnrolled] = useState(EMPTY);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");
  const [fillNums, setFillNums] = useState({ ...EMPTY });

  const [step, setStep] = useState("list");           // list | preview
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [confirmUpdate, setConfirmUpdate] = useState(null);
  const [draftPrompt, setDraftPrompt] = useState(false);
  const touched = useRef(false);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { localStorage.setItem("pm_bulk_ym", JSON.stringify({ year, month })); }, [year, month]);

  const periodKey = periodMode === "month" ? `M-${year}-${month}` : `R-${rangeStart}-${rangeEnd}`;
  const draftKey = `pm_bulk_draft_${periodKey}`;

  const days = useMemo(() => {
    const arr = [];
    if (periodMode === "month") {
      const n = new Date(year, month, 0).getDate();
      for (let d = 1; d <= n; d++) {
        const dt = new Date(year, month - 1, d);
        arr.push({ day: d, dow: dt.getDay(), iso: isoDate(dt) });
      }
    } else {
      const s = new Date(rangeStart + "T00:00:00");
      const e = new Date(rangeEnd + "T00:00:00");
      let cur = new Date(s);
      let guard = 0;
      while (cur <= e && guard < 31) {
        arr.push({ day: cur.getDate(), dow: cur.getDay(), iso: isoDate(cur) });
        cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
        guard++;
      }
    }
    return arr;
  }, [periodMode, year, month, rangeStart, rangeEnd]);

  const buildDefaults = useCallback((savedMap, holSet) => {
    const map = {};
    days.forEach((d) => {
      if (savedMap[d.iso]) { map[d.iso] = savedMap[d.iso]; return; }
      const isHol = d.dow === 0 || holSet.has(d.iso);
      map[d.iso] = {
        day_type: isHol ? "holiday" : "mdm",
        students: { ...EMPTY },
        payment_mode: "dbt",
        holiday_reason: d.dow === 0 ? "Sunday" : "Government Holiday",
        saved: false, savedSnap: null,
      };
    });
    return map;
  }, [days]);

  const load = useCallback(async () => {
    setReady(false);
    setStep("list"); setResult(null);
    const start = days[0]?.iso;
    const end = days[days.length - 1]?.iso;
    if (!start) { setReady(true); return; }
    const [{ data }, { data: hol }] = await Promise.all([
      api.get("/entries", { params: { start, end } }),
      api.get("/holidays", { params: { start, end } }),
    ]);
    const savedMap = {};
    (data.entries || []).forEach((e) => {
      const students = { ...EMPTY, ...(e.students || {}) };
      savedMap[e.date] = {
        day_type: e.day_type || "mdm", students,
        payment_mode: e.payment_mode || "dbt",
        holiday_reason: e.holiday_reason || "Government Holiday",
        saved: true, savedSnap: { day_type: e.day_type || "mdm", students, payment_mode: e.payment_mode || null, holiday_reason: e.holiday_reason || null },
      };
    });
    const holSet = new Set(hol.dates || []);
    setAdminHolidays(holSet);
    setRows(buildDefaults(savedMap, holSet));
    touched.current = false;
    // draft?
    try { if (localStorage.getItem(draftKey)) setDraftPrompt(true); else setDraftPrompt(false); } catch { /* noop */ }
    setReady(true);
  }, [days, buildDefaults, draftKey]);

  useEffect(() => {
    (async () => {
      await loadConfig();
      try { const { data } = await api.get("/profile"); if (data.profile?.enrolled) setEnrolled((p) => ({ ...p, ...data.profile.enrolled })); } catch { /* noop */ }
      api.post("/analytics/event", { events: [{ event: "bulk_opened" }] }).catch(() => {});
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  // persist draft after user interaction
  useEffect(() => {
    if (!ready || !touched.current) return;
    try { localStorage.setItem(draftKey, JSON.stringify(rows)); } catch { /* noop */ }
  }, [rows, ready, draftKey]);

  const markTouched = () => { touched.current = true; };
  const clearDraft = () => { try { localStorage.removeItem(draftKey); } catch { /* noop */ } };

  const restoreDraft = () => {
    try {
      const d = JSON.parse(localStorage.getItem(draftKey));
      if (d) { setRows(d); touched.current = true; toast.success("Draft restored"); }
    } catch { /* noop */ }
    setDraftPrompt(false);
  };

  // ---- editing helpers ----
  const patchRow = (iso, patch) => { markTouched(); setRows((r) => ({ ...r, [iso]: { ...r[iso], ...patch } })); };
  const setCell = (iso, cat, val) => {
    markTouched();
    const v = Math.max(0, Math.min(9999, parseInt(val || 0, 10) || 0));
    setRows((r) => ({ ...r, [iso]: { ...r[iso], students: { ...r[iso].students, [cat]: v } } }));
  };
  const setDayType = (iso, dt) => patchRow(iso, { day_type: dt });

  const copyPrev = (iso) => {
    const idx = days.findIndex((d) => d.iso === iso);
    if (idx <= 0) { toast.error("No previous day"); return; }
    const prev = rows[days[idx - 1].iso];
    if (!prev) return;
    patchRow(iso, { day_type: prev.day_type, students: { ...prev.students }, payment_mode: prev.payment_mode });
    toast.success(t("bulk_copy_prev"));
  };

  const fillWorking = (nums) => {
    markTouched();
    setRows((r) => {
      const nr = { ...r };
      days.forEach((d) => {
        if (d.iso > todayIso) return;
        const row = nr[d.iso];
        if (row.day_type === "holiday") return;
        if (sumStudents(row.students) > 0) return; // only empties
        nr[d.iso] = { ...row, students: { ...nums } };
      });
      return nr;
    });
    toast.success("Filled empty working days");
  };

  const markSundays = () => {
    markTouched();
    setRows((r) => {
      const nr = { ...r };
      days.forEach((d) => { if (d.dow === 0) nr[d.iso] = { ...nr[d.iso], day_type: "holiday", holiday_reason: "Sunday" }; });
      return nr;
    });
    toast.success(t("bulk_mark_sundays"));
  };

  // ---- per-day compute ----
  const dayMeta = useCallback((iso) => {
    try {
      const r = calculateReport(iso, { ...EMPTY, primary: 1 });
      const emojis = Object.values(r.categories.primary.items || {}).map((it) => it.emoji).filter(Boolean);
      return {
        name: lang === "or" ? r.weekday_name_od : r.weekday_name,
        wd: r.weekday_number, meals: emojis.join(""), menu: r.menu_display,
        laddoo: r.laddoo_active, egg: /egg|anda|ଅଣ୍ଡା/i.test(r.menu_display || ""),
      };
    } catch { return { name: "", wd: new Date(iso + "T00:00:00").getDay(), meals: "", menu: "", laddoo: false, egg: false }; }
  }, [lang]);

  const costKey = (iso, st) => `${iso}|${CATEGORIES.map((c) => parseInt(st?.[c] || 0, 10) || 0).join(",")}`;
  useEffect(() => {
    const need = Object.entries(rows).filter(([iso, r]) => r && r.day_type !== "holiday" && sumStudents(r.students) > 0 && !costMap[costKey(iso, r.students)]);
    if (!need.length) return undefined;
    const id = setTimeout(async () => {
      try {
        const { data } = await api.post("/calc/batch", { rows: need.slice(0, 62).map(([iso, r]) => ({ date: iso, students: r.students })) });
        setCostMap((m) => {
          const n = { ...m };
          data.rows.forEach((x, i) => { n[costKey(need[i][0], need[i][1].students)] = { cost: x.grand_total, rice: x.total_rice_kg }; });
          return n;
        });
      } catch { /* keep previous totals */ }
    }, 600);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const dayCalc = (iso, row) => {
    if (!row || row.day_type === "holiday") return { total: 0, cost: 0, rice: 0 };
    const total = sumStudents(row.students);
    if (total === 0) return { total: 0, cost: 0, rice: 0 };
    const c = costMap[costKey(iso, row.students)];
    return { total, cost: c ? c.cost : 0, rice: c ? c.rice : 0 };
  };

  const dayStatus = (d, row) => {
    if (row.day_type === "holiday") return "grey";
    const total = sumStudents(row.students);
    if (total === 0) return "red";
    return row.day_type === "dry_ration" ? "yellow" : "green";
  };

  // ---- counts ----
  const counts = useMemo(() => {
    let readyN = 0, missing = 0, holidays = 0;
    days.forEach((d) => {
      const row = rows[d.iso]; if (!row) return;
      if (row.day_type === "holiday") { holidays++; return; }
      if (sumStudents(row.students) > 0 && d.iso <= todayIso) readyN++;
      else if (d.iso <= todayIso) missing++;
    });
    return { readyN, missing, holidays };
  }, [days, rows, todayIso]);

  const canSave = hasAccess;

  // ---- filter ----
  const visibleDays = days.filter((d) => {
    const row = rows[d.iso]; if (!row) return false;
    if (filter === "all") return true;
    if (filter === "missing") return row.day_type !== "holiday" && sumStudents(row.students) === 0 && d.iso <= todayIso;
    if (filter === "mdm") return row.day_type === "mdm";
    if (filter === "dry") return row.day_type === "dry_ration";
    if (filter === "holiday") return row.day_type === "holiday";
    return true;
  });

  // ---- preview ----
  const previewData = useMemo(() => {
    const items = [];
    const totals = { mdmDays: 0, dryDays: 0, holidays: 0, cooking: 0, dryAmt: 0, dbt: 0, cash: 0, rice: 0 };
    const errors = [];
    days.forEach((d) => {
      const row = rows[d.iso]; if (!row) return;
      const meta = dayMeta(d.iso);
      const future = d.iso > todayIso;
      if (row.day_type === "holiday") {
        totals.holidays++;
        items.push({ iso: d.iso, day: d.day, name: meta.name, dt: "holiday", total: 0, cost: 0, rice: 0, future });
        return;
      }
      const c = dayCalc(d.iso, row);
      if (c.total === 0) { if (!future) errors.push({ iso: d.iso, day: d.day, msg: "No student numbers" }); return; }
      if (future) { errors.push({ iso: d.iso, day: d.day, msg: "Future date — cannot save" }); return; }
      if (row.day_type === "dry_ration") {
        totals.dryDays++; totals.dryAmt += c.cost;
        if (row.payment_mode === "cash") totals.cash += c.cost; else totals.dbt += c.cost;
      } else { totals.mdmDays++; totals.cooking += c.cost; }
      totals.rice += c.rice;
      items.push({ iso: d.iso, day: d.day, name: meta.name, dt: row.day_type, total: c.total, cost: c.cost, rice: c.rice, mode: row.payment_mode, future });
    });
    totals.cooking = Math.round(totals.cooking * 100) / 100;
    totals.dryAmt = Math.round(totals.dryAmt * 100) / 100;
    totals.rice = Math.round(totals.rice * 100) / 100;
    return { items, totals, errors };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, rows, dayMeta, todayIso, costMap]);

  // ---- actions ----
  const goPreview = () => {
    if (!canSave) { openUpgrade(); return; }
    api.post("/analytics/event", { events: [{ event: "bulk_preview", props: { days: counts.readyN } }] }).catch(() => {});
    setStep("preview"); window.scrollTo(0, 0);
  };

  const buildPayload = () => days.map((d) => {
    const row = rows[d.iso];
    if (!row || d.iso > todayIso) return null;
    if (row.day_type === "holiday") return { date: d.iso, day_type: "holiday", holiday_reason: row.holiday_reason, students: EMPTY };
    if (sumStudents(row.students) === 0) return null;
    return { date: d.iso, day_type: row.day_type, students: row.students, payment_mode: row.payment_mode };
  }).filter(Boolean);

  const changedSavedCount = () => days.reduce((a, d) => {
    const row = rows[d.iso];
    if (!row || !row.saved || !row.savedSnap) return a;
    const snap = row.savedSnap;
    const diff = JSON.stringify(snap.students) !== JSON.stringify(row.students) || snap.day_type !== row.day_type
      || (snap.payment_mode || "dbt") !== (row.payment_mode || "dbt");
    return a + (diff ? 1 : 0);
  }, 0);

  const doSave = async (updateSaved) => {
    const entries = buildPayload();
    if (entries.length === 0) { toast.error("Nothing to save."); return; }
    setConfirmUpdate(null);
    setSaving(true); setProgress(8);
    const tick = setInterval(() => setProgress((p) => Math.min(90, p + 9)), 220);
    try {
      const { data } = await api.post("/entries/bulk", { entries, update_saved: updateSaved });
      clearInterval(tick); setProgress(100);
      setResult(data);
      clearDraft();
      api.post("/analytics/event", { events: [{ event: "bulk_save", props: { days: data.saved + data.updated } }] }).catch(() => {});
      await load();
      setStep("preview");
    } catch (e) {
      clearInterval(tick); setProgress(0);
      if (!handle402(e)) toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const saveAll = () => {
    if (!canSave) { openUpgrade(); return; }
    const n = changedSavedCount();
    if (n > 0) { setConfirmUpdate(n); return; }
    doSave(true);
  };

  const repMonth = periodMode === "month" ? month : (new Date(rangeStart + "T00:00:00").getMonth() + 1);
  const repYear = periodMode === "month" ? year : (new Date(rangeStart + "T00:00:00").getFullYear());

  const downloadMonth = async (fmt) => {
    if (!canSave) { openUpgrade(); return; }
    try { await downloadReport("mdcf", fmt, { year: repYear, month: repMonth }); toast.success(`${fmt === "excel" ? "Excel" : "PDF"} downloaded`); }
    catch (e) { if (!handle402(e)) toast.error("Could not download"); }
  };

  const downloadTemplate = async () => {
    if (!canSave) { openUpgrade(); return; }
    try {
      const res = await api.get("/entries/bulk/template", { params: { year: repYear, month: repMonth }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `BulkEntry_${repMonth}-${repYear}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { if (!handle402(e)) toast.error("Could not download template"); }
  };

  const onUpload = async (ev) => {
    const file = ev.target.files?.[0]; ev.target.value = "";
    if (!file) return;
    if (!canSave) { openUpgrade(); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/entries/bulk/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      let applied = 0, errs = 0;
      markTouched();
      setRows((r) => {
        const nr = { ...r };
        (data.rows || []).forEach((row) => {
          if (!row.date || !nr[row.date]) { if (row.error) errs++; return; }
          if (row.error) { nr[row.date] = { ...nr[row.date], error: row.error }; errs++; return; }
          nr[row.date] = {
            ...nr[row.date], error: null, day_type: row.day_type,
            students: { ...EMPTY, ...(row.students || {}) },
            payment_mode: row.payment_mode || "dbt",
            holiday_reason: row.holiday_reason || nr[row.date].holiday_reason,
          };
          applied++;
        });
        return nr;
      });
      toast.success(`${applied} row(s) loaded${errs ? ` · ${errs} error(s)` : ""}`);
    } catch (e) { if (!handle402(e)) toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  if (!ready) return <Loader />;

  return (
    <div className="space-y-4 pb-40">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-2xl md:text-3xl font-heading font-extrabold" data-testid="bulk-title">{t("bulk_entry")}</h1>
        {!canSave && <span className="text-xs font-bold bg-thali-saffron/15 text-thali-saffron rounded-full px-3 py-1 flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> Demo</span>}
      </div>

      {!canSave && (
        <div className="card-thali bg-thali-saffron/10 border border-thali-saffron/30 text-sm" data-testid="bulk-demo-banner">
          🔒 {t("bulk_demo_note")}
        </div>
      )}

      {step === "list" && (
        <>
          {draftPrompt && (
            <div className="card-thali bg-thali-turmeric/15 border border-thali-turmericdark/30 flex items-center justify-between gap-2 text-sm" data-testid="bulk-draft-prompt">
              <span>You have unsaved bulk entries — Continue?</span>
              <div className="flex gap-2">
                <button onClick={restoreDraft} className="btn-3d-green text-xs px-3 py-2" data-testid="bulk-draft-restore">Restore</button>
                <button onClick={() => { clearDraft(); setDraftPrompt(false); }} className="btn-ghost text-xs px-3 py-2" data-testid="bulk-draft-discard">Discard</button>
              </div>
            </div>
          )}

          {/* Step 1 — period */}
          <div className="card-thali space-y-3" data-testid="bulk-period">
            <div className="flex gap-2">
              <button onClick={() => setPeriodMode("month")} className={`flex-1 min-h-[44px] rounded-xl font-semibold text-sm border-2 ${periodMode === "month" ? "bg-thali-green text-white border-thali-green" : "bg-white border-thali-turmeric/30 text-thali-ink/70"}`} data-testid="bulk-mode-month">{t("bulk_month_mode")}</button>
              <button onClick={() => setPeriodMode("range")} className={`flex-1 min-h-[44px] rounded-xl font-semibold text-sm border-2 ${periodMode === "range" ? "bg-thali-green text-white border-thali-green" : "bg-white border-thali-turmeric/30 text-thali-ink/70"}`} data-testid="bulk-mode-range">{t("bulk_range_mode")}</button>
            </div>
            {periodMode === "month" ? (
              <div className="flex items-center gap-3">
                <button onClick={() => { const d = new Date(year, month - 2, 1); setYear(d.getFullYear()); setMonth(d.getMonth() + 1); }} className="w-11 h-11 rounded-lg bg-thali-cream grid place-items-center" data-testid="bulk-prev"><ChevronLeft /></button>
                <div className="font-heading font-bold text-lg flex-1 text-center" data-testid="bulk-month-label">{MONTH_NAMES[month]} {year}</div>
                <button onClick={() => { const d = new Date(year, month, 1); setYear(d.getFullYear()); setMonth(d.getMonth() + 1); }} className="w-11 h-11 rounded-lg bg-thali-cream grid place-items-center" data-testid="bulk-next"><ChevronRight /></button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm font-semibold">{t("bulk_from")}<input type="date" className="input-thali mt-1" value={rangeStart} max={rangeEnd} onChange={(e) => setRangeStart(e.target.value)} data-testid="bulk-range-start" /></label>
                <label className="text-sm font-semibold">{t("bulk_to")}<input type="date" className="input-thali mt-1" value={rangeEnd} min={rangeStart} onChange={(e) => setRangeEnd(e.target.value)} data-testid="bulk-range-end" /></label>
                {days.length >= 31 && <p className="col-span-2 text-xs text-amber-700">{t("bulk_range_max")}</p>}
              </div>
            )}
          </div>

          {/* Step 3 — speed tools */}
          <div className="card-thali space-y-3" data-testid="bulk-speed-tools">
            <div className="text-xs font-bold text-thali-ink/60 uppercase tracking-wide">{t("bulk_speed_tools")}</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => (canSave ? fillWorking(enrolled) : openUpgrade())} className="btn-ghost text-xs" data-testid="bulk-use-enrolled"><Users2 className="w-4 h-4" /> {t("bulk_use_enrolled")}</button>
              <button onClick={markSundays} className="btn-ghost text-xs" data-testid="bulk-mark-sundays"><CalendarOff className="w-4 h-4" /> {t("bulk_mark_sundays")}</button>
              <button onClick={downloadTemplate} className="btn-ghost text-xs" data-testid="bulk-download-template"><FileDown className="w-4 h-4" /> 📥 {t("bulk_download_template")}</button>
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-ghost text-xs" data-testid="bulk-upload-excel"><Upload className="w-4 h-4" /> 📤 {uploading ? "…" : t("bulk_upload_excel")}</button>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onUpload} data-testid="bulk-file-input" />
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              <div className="text-xs font-semibold text-thali-ink/70 w-full">{t("bulk_fill_working")}:</div>
              {CATEGORIES.map((c) => (
                <div key={c} className="flex flex-col">
                  <span className="text-[10px] font-bold" style={{ color: CAT_COLOR[c] }}>{CAT_SHORT[c]}</span>
                  <input inputMode="numeric" value={fillNums[c] || ""} placeholder="0" onChange={(e) => setFillNums((p) => ({ ...p, [c]: Math.max(0, Math.min(9999, parseInt(e.target.value || 0, 10) || 0)) }))} className="w-14 h-11 text-center rounded-lg border border-thali-turmeric/40 font-num" data-testid={`bulk-fill-${c}`} />
                </div>
              ))}
              <button onClick={() => (canSave ? fillWorking(fillNums) : openUpgrade())} className="btn-3d-green text-xs px-3 h-11" data-testid="bulk-fill-apply">{t("bulk_apply")}</button>
            </div>
          </div>

          {/* Filter */}
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="bulk-filter">
            {[["all", t("all")], ["missing", t("bulk_missing")], ["mdm", "🍛 MDM"], ["dry", "🌾 " + t("dry_ration_report")], ["holiday", "🏖️ " + t("holiday")]].map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)} className={`whitespace-nowrap text-xs font-bold rounded-full px-3 py-1.5 border ${filter === k ? "bg-thali-green text-white border-thali-green" : "bg-white text-thali-ink/60 border-thali-turmeric/30"}`} data-testid={`bulk-filter-${k}`}>{label}</button>
            ))}
          </div>

          {/* Step 2 — day cards */}
          <div className="space-y-2" data-testid="bulk-day-list">
            {visibleDays.map((d) => (
              <DayCard key={d.iso} d={d} row={rows[d.iso]} meta={dayMeta(d.iso)} calc={dayCalc(d.iso, rows[d.iso])}
                status={dayStatus(d, rows[d.iso])} expanded={expanded === d.iso} future={d.iso > todayIso}
                onToggle={() => setExpanded(expanded === d.iso ? null : d.iso)}
                onType={(dt) => setDayType(d.iso, dt)} onCell={(c, v) => setCell(d.iso, c, v)}
                onPay={(m) => patchRow(d.iso, { payment_mode: m })} onReason={(r) => patchRow(d.iso, { holiday_reason: r })}
                onCopy={() => copyPrev(d.iso)} t={t} />
            ))}
          </div>

          {/* Sticky summary bar */}
          <div className="fixed left-0 right-0 bottom-[80px] md:bottom-4 z-30 px-4" data-testid="bulk-summary-bar">
            <div className="max-w-3xl mx-auto bg-white/95 backdrop-blur rounded-2xl shadow-2xl border border-thali-turmeric/30 p-2.5 flex items-center gap-3">
              <div className="text-xs font-semibold flex-1 leading-tight" data-testid="bulk-summary-text">
                ✅ {counts.readyN} {t("bulk_ready")} · ⚠️ {counts.missing} {t("bulk_missing")} · 🏖️ {counts.holidays} {t("bulk_holidays")}
              </div>
              <button onClick={goPreview} className="btn-3d-saffron text-sm px-4" data-testid="bulk-preview-btn"><Calculator className="w-4 h-4" /> {t("bulk_calc_preview")}</button>
            </div>
          </div>
        </>
      )}

      {step === "preview" && (
        <PreviewStep data={previewData} result={result} saving={saving} progress={progress}
          t={t} onBack={() => { setStep("list"); setResult(null); }} onSave={saveAll}
          onFix={(iso) => { setStep("list"); setExpanded(iso); setTimeout(() => document.querySelector(`[data-testid='bulk-card-${iso}']`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 80); }}
          onViewMdcf={() => nav("/app/reports")} onPdf={() => downloadMonth("pdf")} onExcel={() => downloadMonth("excel")} />
      )}

      {confirmUpdate != null && (
        <div className="fixed inset-0 z-[90] flex items-end md:items-center justify-center" data-testid="bulk-update-confirm">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmUpdate(null)} />
          <div className="relative w-full md:max-w-sm bg-thali-paper rounded-t-3xl md:rounded-3xl p-5 shadow-2xl">
            <div className="font-heading font-extrabold text-lg">{confirmUpdate} {t("bulk_update_confirm")}</div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => doSave(true)} className="btn-3d-green flex-1" data-testid="bulk-update-yes">{t("bulk_update_btn")}</button>
              <button onClick={() => doSave(false)} className="btn-ghost flex-1" data-testid="bulk-update-skip">{t("bulk_skip_btn")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusDot({ status, saved }) {
  const color = { green: "bg-thali-green", yellow: "bg-amber-400", grey: "bg-thali-ink/25", red: "bg-red-500" }[status];
  return <span className={`w-3.5 h-3.5 rounded-full ${color} ${saved ? "ring-2 ring-blue-500 ring-offset-1" : ""}`} title={status} />;
}

function DayCard({ d, row, meta, calc, status, expanded, future, onToggle, onType, onCell, onPay, onReason, onCopy, t }) {
  if (!row) return null;
  const changed = row.saved && row.savedSnap && (JSON.stringify(row.savedSnap.students) !== JSON.stringify(row.students) || row.savedSnap.day_type !== row.day_type);
  const dd = String(d.day).padStart(2, "0");
  return (
    <div className={`rounded-2xl border ${row.error ? "border-red-400 bg-red-50/40" : "border-thali-turmeric/30 bg-white"}`} data-testid={`bulk-card-${d.iso}`}>
      <button onClick={onToggle} className="w-full text-left p-3 flex items-center gap-2" data-testid={`bulk-card-head-${d.day}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap font-semibold text-sm">
            <span className="font-num">{dd}-{String(new Date(d.iso).getMonth() + 1).padStart(2, "0")}</span>
            <span>{DAY_EMOJI[meta.wd]} {meta.name}</span>
            {meta.meals && <span className="text-base leading-none">{meta.meals}</span>}
            {meta.laddoo && <span className="text-[10px] font-bold bg-thali-turmeric/30 rounded-full px-1.5">🧁</span>}
            {meta.egg && <span className="text-[10px] font-bold bg-orange-100 text-orange-700 rounded-full px-1.5">🥚</span>}
          </div>
          <div className="text-xs text-thali-ink/60 mt-0.5">
            {row.day_type === "holiday" ? `🏖️ ${row.holiday_reason}` : `${calc.total} ${t("students")} · ${calc.cost ? formatCurrency(calc.cost) : "—"}`}
            {row.saved && <span className="ml-2 text-[10px] font-bold text-blue-600">{changed ? t("bulk_will_update") : t("bulk_saved_chip")}</span>}
          </div>
        </div>
        <StatusDot status={status} saved={row.saved} />
        <ChevronDown className={`w-4 h-4 text-thali-ink/40 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-thali-turmeric/20 pt-3">
          <div className="flex gap-1.5">
            {[["mdm", "🍛 MDM"], ["dry_ration", "🌾 Dry"], ["holiday", "🏖️ Holiday"]].map(([k, label]) => (
              <button key={k} onClick={() => onType(k)} className={`flex-1 min-h-[40px] rounded-lg text-xs font-bold border-2 ${row.day_type === k ? "bg-thali-green text-white border-thali-green" : "bg-white border-thali-turmeric/30 text-thali-ink/60"}`} data-testid={`bulk-type-${d.day}-${k}`}>{label}</button>
            ))}
          </div>

          {row.day_type === "holiday" ? (
            <select className="input-thali" value={row.holiday_reason} onChange={(e) => onReason(e.target.value)} data-testid={`bulk-reason-${d.day}`}>
              {HOLIDAY_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-2">
                {CATEGORIES.map((c) => (
                  <div key={c} className="flex flex-col">
                    <span className="text-[10px] font-bold text-center" style={{ color: CAT_COLOR[c] }}>{CAT_SHORT[c]}</span>
                    <input inputMode="numeric" pattern="[0-9]*" value={row.students[c] || ""} placeholder="0"
                      onChange={(e) => onCell(c, e.target.value)}
                      className="w-full h-12 text-center rounded-lg border border-thali-turmeric/40 font-num text-base bg-white"
                      style={{ borderBottom: `3px solid ${CAT_COLOR[c]}` }} data-testid={`bulk-input-${d.day}-${c}`} />
                  </div>
                ))}
              </div>
              {row.day_type === "dry_ration" && (
                <div className="flex gap-2">
                  <button onClick={() => onPay("dbt")} className={`flex-1 min-h-[40px] rounded-lg text-xs font-bold border-2 ${row.payment_mode === "dbt" ? "bg-thali-green text-white border-thali-green" : "bg-white border-thali-turmeric/30"}`} data-testid={`bulk-pay-${d.day}-dbt`}>🏦 {t("bank_dbt")}</button>
                  <button onClick={() => onPay("cash")} className={`flex-1 min-h-[40px] rounded-lg text-xs font-bold border-2 ${row.payment_mode === "cash" ? "bg-thali-green text-white border-thali-green" : "bg-white border-thali-turmeric/30"}`} data-testid={`bulk-pay-${d.day}-cash`}>💵 {t("cash_hand")}</button>
                </div>
              )}
            </>
          )}
          {future && <div className="text-xs text-amber-700">⏳ {t("future_preview")}</div>}
          {row.error && <div className="text-xs text-red-600">{row.error}</div>}
          <button onClick={onCopy} className="btn-ghost text-xs w-full" data-testid={`bulk-copy-${d.day}`}><CopyPlus className="w-4 h-4" /> {t("bulk_copy_prev")}</button>
        </div>
      )}
    </div>
  );
}

function PreviewStep({ data, result, saving, progress, t, onBack, onSave, onFix, onViewMdcf, onPdf, onExcel }) {
  const { items, totals, errors } = data;
  return (
    <div className="space-y-4 pb-40" data-testid="bulk-preview">
      <button onClick={onBack} className="btn-ghost text-sm" data-testid="bulk-back"><ArrowLeft className="w-4 h-4" /> {t("bulk_back_edit")}</button>
      <h2 className="text-xl font-heading font-extrabold">{t("bulk_preview_title")}</h2>

      {errors.length > 0 && (
        <div className="card-thali bg-red-50 border border-red-300" data-testid="bulk-preview-errors">
          <div className="font-bold text-red-700 text-sm mb-2">⚠️ {t("bulk_errors_top")}</div>
          <div className="space-y-1">
            {errors.map((e) => (
              <div key={e.iso} className="flex items-center justify-between text-sm">
                <span>{String(e.day).padStart(2, "0")} — {e.msg}</span>
                <button onClick={() => onFix(e.iso)} className="btn-ghost text-xs px-2 py-1" data-testid={`bulk-fix-${e.day}`}>{t("bulk_fix")}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Month totals */}
      <div className="card-thali" data-testid="bulk-month-totals">
        <div className="font-heading font-bold mb-2">{t("bulk_month_totals")}</div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <Total label={t("bulk_mdm_days")} value={totals.mdmDays} />
          <Total label={t("bulk_dry_days")} value={totals.dryDays} />
          <Total label={t("bulk_holidays")} value={totals.holidays} />
          <Total label={t("bulk_total_rice")} value={`${totals.rice} kg`} />
          <Total label={t("bulk_total_cooking")} value={formatCurrency(totals.cooking)} green />
          <Total label={t("bulk_dry_amount")} value={formatCurrency(totals.dryAmt)} />
        </div>
        {totals.dryAmt > 0 && <div className="text-xs text-thali-ink/60 mt-2">🏦 DBT {formatCurrency(totals.dbt)} · 💵 Cash {formatCurrency(totals.cash)}</div>}
      </div>

      <div className="card-thali p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[420px]">
          <thead><tr className="bg-thali-green text-white"><th className="p-2 text-left">Date</th><th className="p-2">Type</th><th className="p-2">Students</th><th className="p-2 text-right">Cost</th><th className="p-2 text-right pr-3">Rice</th></tr></thead>
          <tbody>
            {items.filter((i) => !i.future).map((i) => (
              <tr key={i.iso} className="border-t border-thali-turmeric/10" data-testid={`bulk-preview-row-${i.day}`}>
                <td className="p-2 font-semibold whitespace-nowrap">{String(i.day).padStart(2, "0")} {i.name?.slice(0, 3)}</td>
                <td className="p-2 text-center text-xs">{i.dt === "holiday" ? "🏖️" : i.dt === "dry_ration" ? `🌾${i.mode === "cash" ? "💵" : "🏦"}` : "🍛"}</td>
                <td className="p-2 text-center font-num">{i.total}</td>
                <td className="p-2 text-right font-num">{i.cost ? formatCurrency(i.cost) : "—"}</td>
                <td className="p-2 text-right pr-3 font-num">{i.rice ? `${i.rice}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {result ? (
        <div className="card-thali bg-thali-green/10 border border-thali-green/30 text-center space-y-3" data-testid="bulk-result">
          <CheckCircle2 className="w-10 h-10 text-thali-green mx-auto" />
          <div className="font-heading font-extrabold text-lg">{result.saved + result.updated} {t("bulk_saved_result")}{result.skipped ? ` · ${result.skipped} ${t("bulk_skipped_result")}` : ""}</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button onClick={onViewMdcf} className="btn-3d-green text-sm" data-testid="bulk-view-mdcf"><FileText className="w-4 h-4" /> {t("bulk_view_mdcf")}</button>
            <button onClick={onPdf} className="btn-3d-saffron text-sm" data-testid="bulk-result-pdf"><FileDown className="w-4 h-4" /> {t("download_pdf")}</button>
            <button onClick={onExcel} className="btn-ghost text-sm" data-testid="bulk-result-excel"><Sheet className="w-4 h-4" /> {t("download_excel")}</button>
          </div>
        </div>
      ) : (
        <div className="fixed left-0 right-0 bottom-[80px] md:bottom-4 z-30 px-4">
          <div className="max-w-3xl mx-auto">
            {saving && <div className="h-2 bg-thali-cream rounded-full overflow-hidden mb-2"><div className="h-full bg-thali-green transition-all" style={{ width: `${progress}%` }} data-testid="bulk-progress" /></div>}
            <button onClick={onSave} disabled={saving} className="btn-3d-green w-full text-lg" data-testid="bulk-save-all"><Save className="w-5 h-5" /> {saving ? t("bulk_saving") : t("bulk_save_all")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Total({ label, value, green }) {
  return (
    <div className="rounded-lg bg-thali-cream/60 p-2">
      <div className="text-[11px] text-thali-ink/60">{label}</div>
      <div className={`font-num font-bold ${green ? "text-thali-green" : ""}`}>{value}</div>
    </div>
  );
}
